import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Toolbar, Divider, useTheme, useMediaQuery, Collapse, Badge } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { EXPENSE_CLAIMS_ENABLED, HIGH_FIVES_ENABLED } from '../constants/api';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import type { User } from '../types';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EmailIcon from '@mui/icons-material/Email';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import HistoryIcon from '@mui/icons-material/History';
import GavelIcon from '@mui/icons-material/Gavel';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PolicyIcon from '@mui/icons-material/Policy';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FrontHandIcon from '@mui/icons-material/FrontHand';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 64;

/** Shared selected/hover treatment for top-level and nested sidebar items (right accent bar). */
const navSelectedSx: SxProps<Theme> = {
  '&.Mui-selected': {
    backgroundColor: 'rgba(102, 126, 234, 0.15)',
    borderRight: '3px solid',
    borderColor: 'primary.main',
    '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
  },
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
};

const nestedNavItemSx: SxProps<Theme> = {
  pl: 4,
  ...navSelectedSx,
};

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  isAdmin: boolean;
  isManager?: boolean;
}

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
}

export default function Sidebar({ open, onToggle, isAdmin, isManager = false }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, outstandingTasksCount, openDisputesCount, profileSetupStatus } = useAuth();
  const userRoles = user?.roles || [];
  const [organogramOpen, setOrganogramOpen] = useState(() => location.pathname.startsWith('/organogram'));
  const [workforceOpen, setWorkforceOpen] = useState(() => location.pathname.startsWith('/workforce/'));
  const [teamSectionOpen, setTeamSectionOpen] = useState(
    () => location.pathname === '/dashboard/team' || location.pathname === '/team-roster' || location.pathname === '/team-leave' || location.pathname === '/team-expenses'
  );
  const [organisationSectionOpen, setOrganisationSectionOpen] = useState(
    () => location.pathname === '/dashboard/organisation' || location.pathname === '/leave/organisation' || location.pathname === '/expenses/organisation'
  );
  const [auditSectionOpen, setAuditSectionOpen] = useState(
    () => location.pathname === '/checkins/audit-log' || location.pathname === '/reviews/audit-log' || location.pathname.startsWith('/idp-audit-log') ||
      location.pathname === '/users/audit-log' || location.pathname === '/leave/audit-log' || location.pathname === '/expense-claims/audit-log' || location.pathname === '/email-logs'
  );
  const [hasTeamMembers, setHasTeamMembers] = useState(false);
  const drawerContentRef = useRef<HTMLDivElement>(null);

  const isSeniorManager = userRoles?.includes('SeniorManager');
  const userDepartmentNames = user?.departmentNames ?? (user?.departmentName ? [user.departmentName] : []);
  const isFinanceDepartment = userDepartmentNames.some((name) => /finance/i.test(name ?? ''));
  const canAccessOrganisationDashboard = isAdmin || isSeniorManager;
  const canAccessOrganisationLeaveExpenses = isAdmin || isSeniorManager || isFinanceDepartment;
  const canAccessOrganisationSection = canAccessOrganisationDashboard || canAccessOrganisationLeaveExpenses;
  const isReportingExempt = userRoles?.includes('ReportingExempt');
  const accountSetupRestricted = profileSetupStatus?.isRestricted === true;

  // Admin: primary items (no audit logs), then Audit logs as collapsible, then Settings
  const adminPrimaryNavItems: NavItem[] = isAdmin
    ? [
        { label: 'Users', icon: <PeopleIcon />, path: '/users' },
        { label: 'Reference Data', icon: <BusinessIcon />, path: '/reference-data' },
        { label: 'Review Cycles', icon: <AssignmentIcon />, path: '/review-cycles' },
        { label: 'Role Templates', icon: <AssessmentIcon />, path: '/role-templates' },
        { label: 'Disputes', icon: <GavelIcon />, path: '/disputes' },
        { label: 'Imported Leave', icon: <EventAvailableIcon />, path: '/leave/imported-balances' },
      ]
    : [];
  // Audit log sub-items: short labels (no "Audit Log" repetition)
  const adminAuditLogItems: { label: string; path: string }[] = isAdmin
    ? [
        { label: 'Check-In', path: '/checkins/audit-log' },
        { label: 'Review', path: '/reviews/audit-log' },
        { label: 'IDP', path: '/idp-audit-log' },
        { label: 'User', path: '/users/audit-log' },
        { label: 'Leave', path: '/leave/audit-log' },
        ...(EXPENSE_CLAIMS_ENABLED ? [{ label: 'Expenses', path: '/expense-claims/audit-log' as const }] : []),
        { label: 'Emails', path: '/email-logs' },
      ]
    : [];

  // Non-admin: single list (My Audit Log for manager or standard user)
  const baseNavItems: NavItem[] = isManager
    ? [{ label: 'My Audit Log', icon: <HistoryIcon />, path: '/users/audit-log' }]
    : [
        { label: 'My Audit Log', icon: <HistoryIcon />, path: '/users/audit-log' },
      ];
  const navItems: NavItem[] = baseNavItems;

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onToggle(); // Close drawer on mobile after navigation
    }
  };

  // Auto-open Team / Organisation / Organogram / Audit sections when their routes are active
  useEffect(() => {
    const path = location.pathname;
    const shouldOpenOrganogram = path.startsWith('/organogram') && !organogramOpen;
    const shouldOpenWorkforce = path.startsWith('/workforce/') && !workforceOpen;
    const shouldOpenTeam = (path === '/dashboard/team' || path === '/team-roster' || path === '/team-leave' || path === '/team-expenses') && !teamSectionOpen;
    const shouldOpenOrganisation = (path === '/dashboard/organisation' || path === '/leave/organisation' || path === '/expenses/organisation') && !organisationSectionOpen;
    const isAuditPath = path === '/checkins/audit-log' || path === '/reviews/audit-log' || path.startsWith('/idp-audit-log') || path === '/users/audit-log' || path === '/leave/audit-log' || path === '/expense-claims/audit-log' || path === '/email-logs';
    const shouldOpenAudit = isAdmin && isAuditPath && !auditSectionOpen;

    if (shouldOpenOrganogram || shouldOpenWorkforce || shouldOpenTeam || shouldOpenOrganisation || shouldOpenAudit) {
      const timer = setTimeout(() => {
        if (shouldOpenOrganogram) setOrganogramOpen(true);
        if (shouldOpenWorkforce) setWorkforceOpen(true);
        if (shouldOpenTeam) setTeamSectionOpen(true);
        if (shouldOpenOrganisation) setOrganisationSectionOpen(true);
        if (shouldOpenAudit) setAuditSectionOpen(true);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, organogramOpen, workforceOpen, teamSectionOpen, organisationSectionOpen, auditSectionOpen, isAdmin]);

  // Keep the active nav item visible after navigation / section expand (sidebar scrolls independently of the page).
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const root = drawerContentRef.current;
      if (!root) return;
      const selected = root.querySelectorAll<HTMLElement>('.MuiListItemButton-root.Mui-selected');
      // Prefer the deepest (nested) selected item when a section parent is also selected.
      const target = selected[selected.length - 1];
      target?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [
    location.pathname,
    open,
    teamSectionOpen,
    organisationSectionOpen,
    auditSectionOpen,
    organogramOpen,
    workforceOpen,
  ]);

  // Check if user has team members (only for managers)
  // Team Dashboard should only show for users who actually have direct reports
  // This includes admins who happen to have direct reports, but excludes admins without teams
  useEffect(() => {
    const checkTeamMembers = async () => {
      if (!isManager || !user) {
        setHasTeamMembers(false);
        return;
      }

      try {
        const teamResponse = await apiService.get<User[]>(API_ENDPOINTS.USERS.MY_TEAM);
        // Only show Team Dashboard if user actually has direct reports
        const hasMembers = (teamResponse.data?.length || 0) > 0;
        setHasTeamMembers(hasMembers);
      } catch {
        // If API call fails (e.g., user doesn't have permission or has no team), hide Team Dashboard
        // This handles cases where admins have no direct reports
        setHasTeamMembers(false);
      }
    };

    checkTeamMembers();
  }, [isManager, user]);

  const toggleOrganogramMenu = () => setOrganogramOpen(!organogramOpen);
  const toggleWorkforceMenu = () => setWorkforceOpen(!workforceOpen);
  const toggleTeamSection = () => setTeamSectionOpen(!teamSectionOpen);
  const toggleOrganisationSection = () => setOrganisationSectionOpen(!organisationSectionOpen);
  const toggleAuditSection = () => setAuditSectionOpen(!auditSectionOpen);

  const isOrganogramActive = location.pathname.startsWith('/organogram');
  const isWorkforceActive = location.pathname.startsWith('/workforce');
  const isTeamActive = location.pathname === '/organogram/team';
  const isDepartmentActive = location.pathname === '/organogram/department';
  const isBusinessUnitActive = location.pathname === '/organogram/businessunit';
  const isAllActive = location.pathname === '/organogram/all';
  const isWorkforceTeamActive = location.pathname === '/workforce/team';
  const isWorkforceDepartmentActive = location.pathname === '/workforce/department';
  const isWorkforceBusinessUnitActive = location.pathname === '/workforce/businessunit';
  const isWorkforceAllActive = location.pathname === '/workforce/all';

  const isMyDashboardActive = location.pathname === '/dashboard/my';
  const isTeamDashboardActive = location.pathname === '/dashboard/team';
  const isTeamRosterActive = location.pathname === '/team-roster';
  const isTeamLeaveActive = location.pathname === '/team-leave';
  const isTeamExpensesActive = EXPENSE_CLAIMS_ENABLED && location.pathname === '/team-expenses';
  const isTeamSectionActive = isTeamDashboardActive || isTeamRosterActive || isTeamLeaveActive || isTeamExpensesActive;
  const isOrganisationDashboardActive = location.pathname === '/dashboard/organisation';
  const isOrganisationLeaveActive = location.pathname === '/leave/organisation';
  const isOrganisationExpensesActive = EXPENSE_CLAIMS_ENABLED && location.pathname === '/expenses/organisation';
  const isOrganisationSectionActive =
    isOrganisationDashboardActive || isOrganisationLeaveActive || isOrganisationExpensesActive;
  const isAuditSectionActive = isAdmin && (
    location.pathname === '/checkins/audit-log' || location.pathname === '/reviews/audit-log' || location.pathname.startsWith('/idp-audit-log') ||
    location.pathname === '/users/audit-log' || location.pathname === '/leave/audit-log' || location.pathname === '/expense-claims/audit-log' || location.pathname === '/email-logs'
  );

  const isProfileActive = location.pathname === '/profile';
  const isMyReviewsActive = location.pathname === '/my-reviews';
  const isIdpActive = location.pathname.startsWith('/individual-development-plan');
  const isCheckInsActive = location.pathname === '/checkins';
  const isEmailsActive = location.pathname === '/email-logs';
  const isLeaveMeActive = location.pathname === '/leave/me';
  const isExpensesMeActive = location.pathname === '/expenses/me';
  const isGovernanceActive = location.pathname === '/governance';
  const isHighFivesActive = location.pathname === '/high-fives';

  const drawerContent = (
    <Box ref={drawerContentRef}>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'flex-end' : 'center',
          px: [1, 2],
          minHeight: '64px !important',
        }}
      >
        {!isMobile && (
          <IconButton onClick={onToggle} sx={{ color: 'inherit' }}>
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List>
        {/* Me section - Dashboard and Tasks first (home + action inbox), then Profile and rest */}
        {open && <ListSubheader sx={{ lineHeight: 2, fontWeight: 600 }}>Me</ListSubheader>}
        {!accountSetupRestricted && (
        <>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => handleNavigation('/dashboard/my')}
            selected={isMyDashboardActive}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(102, 126, 234, 0.15)',
                borderRight: '3px solid',
                borderColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.25)',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
                color: isMyDashboardActive ? 'primary.main' : 'inherit',
              }}
            >
              <HomeIcon />
            </ListItemIcon>
            <ListItemText
              primary="Dashboard"
              sx={{
                opacity: open ? 1 : 0,
                transition: 'opacity 0.2s',
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => handleNavigation('/my-tasks')}
            selected={location.pathname === '/my-tasks' || location.pathname === '/'}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(102, 126, 234, 0.15)',
                borderRight: '3px solid',
                borderColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.25)',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
                color: (location.pathname === '/my-tasks' || location.pathname === '/') ? 'primary.main' : 'inherit',
              }}
            >
              <Badge badgeContent={outstandingTasksCount > 0 ? outstandingTasksCount : undefined} color="error">
                <TaskAltIcon />
              </Badge>
            </ListItemIcon>
            <ListItemText
              primary="Tasks"
              sx={{
                opacity: open ? 1 : 0,
                transition: 'opacity 0.2s',
              }}
            />
          </ListItemButton>
        </ListItem>
        {HIGH_FIVES_ENABLED && (
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              onClick={() => handleNavigation('/high-fives')}
              selected={isHighFivesActive}
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(102, 126, 234, 0.15)',
                  borderRight: '3px solid',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 126, 234, 0.25)',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                  color: isHighFivesActive ? 'primary.main' : 'inherit',
                }}
              >
                <FrontHandIcon />
              </ListItemIcon>
              <ListItemText
                primary="High Fives"
                sx={{
                  opacity: open ? 1 : 0,
                  transition: 'opacity 0.2s',
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
        </>
        )}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => handleNavigation('/profile')}
            selected={isProfileActive}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(102, 126, 234, 0.15)',
                borderRight: '3px solid',
                borderColor: 'primary.main',
                '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
              },
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: isProfileActive ? 'primary.main' : 'inherit' }}>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary="Profile" sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
          </ListItemButton>
        </ListItem>
        {!accountSetupRestricted && (
        <>
        {!isReportingExempt && (
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              onClick={() => handleNavigation('/my-reviews')}
              selected={isMyReviewsActive}
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(102, 126, 234, 0.15)',
                  borderRight: '3px solid',
                  borderColor: 'primary.main',
                  '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
                },
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: isMyReviewsActive ? 'primary.main' : 'inherit' }}>
                <AssignmentIcon />
              </ListItemIcon>
              <ListItemText primary="Reviews" sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
            </ListItemButton>
          </ListItem>
        )}
        {!isReportingExempt && (
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              onClick={() => handleNavigation('/individual-development-plan')}
              selected={isIdpActive}
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(102, 126, 234, 0.15)',
                  borderRight: '3px solid',
                  borderColor: 'primary.main',
                  '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
                },
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: isIdpActive ? 'primary.main' : 'inherit' }}>
                <SchoolIcon />
              </ListItemIcon>
              <ListItemText primary="Development Plan" sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
            </ListItemButton>
          </ListItem>
        )}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => handleNavigation('/checkins?view=my')}
            selected={isCheckInsActive}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(102, 126, 234, 0.15)',
                borderRight: '3px solid',
                borderColor: 'primary.main',
                '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
              },
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: isCheckInsActive ? 'primary.main' : 'inherit' }}>
              <CheckCircleIcon />
            </ListItemIcon>
            <ListItemText primary="Check-Ins" sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => handleNavigation('/email-logs?view=my-emails')}
            selected={isEmailsActive}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(102, 126, 234, 0.15)',
                borderRight: '3px solid',
                borderColor: 'primary.main',
                '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
              },
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: isEmailsActive ? 'primary.main' : 'inherit' }}>
              <EmailIcon />
            </ListItemIcon>
            <ListItemText primary="Emails" sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => handleNavigation('/leave/me')}
            selected={isLeaveMeActive}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(102, 126, 234, 0.15)',
                borderRight: '3px solid',
                borderColor: 'primary.main',
                '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
              },
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: isLeaveMeActive ? 'primary.main' : 'inherit' }}>
              <EventAvailableIcon />
            </ListItemIcon>
            <ListItemText primary="Leave" sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
          </ListItemButton>
        </ListItem>
        {EXPENSE_CLAIMS_ENABLED && (
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              onClick={() => handleNavigation('/expenses/me')}
              selected={isExpensesMeActive}
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(102, 126, 234, 0.15)',
                  borderRight: '3px solid',
                  borderColor: 'primary.main',
                  '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
                },
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: isExpensesMeActive ? 'primary.main' : 'inherit' }}>
                <ReceiptIcon />
              </ListItemIcon>
              <ListItemText primary="Expenses" sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
            </ListItemButton>
          </ListItem>
        )}
        </>
        )}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => handleNavigation('/governance')}
            selected={isGovernanceActive}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(102, 126, 234, 0.15)',
                borderRight: '3px solid',
                borderColor: 'primary.main',
                '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
              },
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: isGovernanceActive ? 'primary.main' : 'inherit' }}>
              <PolicyIcon />
            </ListItemIcon>
            <ListItemText primary="Governance" sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
          </ListItemButton>
        </ListItem>
        {!accountSetupRestricted && (
        <>
        <Divider sx={{ my: 1 }} />
        {/* Team section - only when manager with direct reports */}
        {isManager && hasTeamMembers && (
          <>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                onClick={toggleTeamSection}
                selected={isTeamSectionActive}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(102, 126, 234, 0.15)',
                    borderRight: '3px solid',
                    borderColor: 'primary.main',
                    '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
                  },
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: isTeamSectionActive ? 'primary.main' : 'inherit' }}>
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText primary="Team" sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
                {open && (teamSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
              </ListItemButton>
            </ListItem>
            <Collapse in={teamSectionOpen && open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton sx={nestedNavItemSx} selected={isTeamDashboardActive} onClick={() => handleNavigation('/dashboard/team')}>
                  <ListItemText primary="Team Dashboard" />
                </ListItemButton>
                <ListItemButton sx={nestedNavItemSx} selected={isTeamRosterActive} onClick={() => handleNavigation('/team-roster')}>
                  <ListItemText primary="My Team" />
                </ListItemButton>
                <ListItemButton sx={nestedNavItemSx} selected={isTeamLeaveActive} onClick={() => handleNavigation('/team-leave')}>
                  <ListItemText primary="Team Leave" />
                </ListItemButton>
                {EXPENSE_CLAIMS_ENABLED && (
                  <ListItemButton sx={nestedNavItemSx} selected={isTeamExpensesActive} onClick={() => handleNavigation('/team-expenses')}>
                    <ListItemText primary="Team Expenses" />
                  </ListItemButton>
                )}
              </List>
            </Collapse>
          </>
        )}

        {/* Organisation section - only when admin or senior manager */}
        {canAccessOrganisationSection && (
          <>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                onClick={toggleOrganisationSection}
                selected={isOrganisationSectionActive}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(102, 126, 234, 0.15)',
                    borderRight: '3px solid',
                    borderColor: 'primary.main',
                    '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
                  },
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: isOrganisationSectionActive ? 'primary.main' : 'inherit' }}>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText primary="Organisation" sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
                {open && (organisationSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
              </ListItemButton>
            </ListItem>
            <Collapse in={organisationSectionOpen && open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {canAccessOrganisationDashboard && (
                  <ListItemButton sx={nestedNavItemSx} selected={isOrganisationDashboardActive} onClick={() => handleNavigation('/dashboard/organisation')}>
                    <ListItemText primary="Organisation Dashboard" />
                  </ListItemButton>
                )}
                {canAccessOrganisationLeaveExpenses && (
                  <ListItemButton sx={nestedNavItemSx} selected={isOrganisationLeaveActive} onClick={() => handleNavigation('/leave/organisation')}>
                    <ListItemText primary="Organisation Leave" />
                  </ListItemButton>
                )}
                {canAccessOrganisationLeaveExpenses && EXPENSE_CLAIMS_ENABLED && (
                  <ListItemButton sx={nestedNavItemSx} selected={isOrganisationExpensesActive} onClick={() => handleNavigation('/expenses/organisation')}>
                    <ListItemText primary="Organisation Expenses" />
                  </ListItemButton>
                )}
              </List>
            </Collapse>
          </>
        )}

        {isAdmin ? (
          <>
            {adminPrimaryNavItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
              return (
                <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={isActive}
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(102, 126, 234, 0.15)',
                        borderRight: '3px solid',
                        borderColor: 'primary.main',
                        '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
                      },
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: isActive ? 'primary.main' : 'inherit' }}>
                      {item.path === '/disputes' && openDisputesCount > 0 ? (
                        <Badge badgeContent={openDisputesCount} color="error">
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    <ListItemText primary={item.label} sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
                  </ListItemButton>
                </ListItem>
              );
            })}
            {/* Audit logs - collapsible with short labels */}
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                onClick={toggleAuditSection}
                selected={isAuditSectionActive}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(102, 126, 234, 0.15)',
                    borderRight: '3px solid',
                    borderColor: 'primary.main',
                    '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
                  },
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: isAuditSectionActive ? 'primary.main' : 'inherit' }}>
                  <HistoryIcon />
                </ListItemIcon>
                <ListItemText primary="Audit logs" sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
                {open && (auditSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
              </ListItemButton>
            </ListItem>
            <Collapse in={auditSectionOpen && open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {adminAuditLogItems.map((a) => {
                  const isActive = location.pathname === a.path || (a.path !== '/email-logs' && location.pathname.startsWith(a.path));
                  return (
                    <ListItemButton key={a.path} sx={nestedNavItemSx} selected={isActive} onClick={() => handleNavigation(a.path)}>
                      <ListItemText primary={a.label} />
                    </ListItemButton>
                  );
                })}
              </List>
            </Collapse>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                onClick={() => handleNavigation('/settings')}
                selected={location.pathname === '/settings'}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(102, 126, 234, 0.15)',
                    borderRight: '3px solid',
                    borderColor: 'primary.main',
                    '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
                  },
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: location.pathname === '/settings' ? 'primary.main' : 'inherit' }}>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText primary="Settings" sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          navItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
            return (
              <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  selected={isActive}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(102, 126, 234, 0.15)',
                      borderRight: '3px solid',
                      borderColor: 'primary.main',
                      '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.25)' },
                    },
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center', color: isActive ? 'primary.main' : 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }} />
                </ListItemButton>
              </ListItem>
            );
          })
        )}

        {/* Organogram menu with submenu */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={toggleOrganogramMenu}
            selected={isOrganogramActive}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(102, 126, 234, 0.15)',
                borderRight: '3px solid',
                borderColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.25)',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
                color: isOrganogramActive ? 'primary.main' : 'inherit',
              }}
            >
              <AccountTreeIcon />
            </ListItemIcon>
            <ListItemText
              primary="Organogram"
              sx={{
                opacity: open ? 1 : 0,
                transition: 'opacity 0.2s',
              }}
            />
            {open && (organogramOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
          </ListItemButton>
        </ListItem>
        <Collapse in={organogramOpen && open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={nestedNavItemSx}
              selected={isTeamActive}
              onClick={() => handleNavigation('/organogram/team')}
            >
              <ListItemText primary="My Team" />
            </ListItemButton>
            <ListItemButton
              sx={nestedNavItemSx}
              selected={isDepartmentActive}
              onClick={() => handleNavigation('/organogram/department')}
            >
              <ListItemText primary="My Department" />
            </ListItemButton>
            <ListItemButton
              sx={nestedNavItemSx}
              selected={isBusinessUnitActive}
              onClick={() => handleNavigation('/organogram/businessunit')}
            >
              <ListItemText primary="My Business Unit" />
            </ListItemButton>
            <ListItemButton
              sx={nestedNavItemSx}
              selected={isAllActive}
              onClick={() => handleNavigation('/organogram/all')}
            >
              <ListItemText primary="All" />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Workforce menu with scope views matching Organogram framing */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={toggleWorkforceMenu}
            selected={isWorkforceActive}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              '&.Mui-selected': {
                backgroundColor: 'rgba(102, 126, 234, 0.15)',
                borderRight: '3px solid',
                borderColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.25)',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
                color: isWorkforceActive ? 'primary.main' : 'inherit',
              }}
            >
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText
              primary="Workforce"
              sx={{
                opacity: open ? 1 : 0,
                transition: 'opacity 0.2s',
              }}
            />
            {open && (workforceOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
          </ListItemButton>
        </ListItem>
        <Collapse in={workforceOpen && open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton sx={nestedNavItemSx} selected={isWorkforceTeamActive} onClick={() => handleNavigation('/workforce/team')}>
              <ListItemText primary="My Team" />
            </ListItemButton>
            <ListItemButton sx={nestedNavItemSx} selected={isWorkforceDepartmentActive} onClick={() => handleNavigation('/workforce/department')}>
              <ListItemText primary="My Department" />
            </ListItemButton>
            <ListItemButton sx={nestedNavItemSx} selected={isWorkforceBusinessUnitActive} onClick={() => handleNavigation('/workforce/businessunit')}>
              <ListItemText primary="My Business Unit" />
            </ListItemButton>
            <ListItemButton sx={nestedNavItemSx} selected={isWorkforceAllActive} onClick={() => handleNavigation('/workforce/all')}>
              <ListItemText primary="All" />
            </ListItemButton>
          </List>
        </Collapse>
        </>
        )}
      </List>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width: open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          mt: '64px', // Account for fixed header
          height: 'calc(100vh - 64px)',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
